import { ConsoleResourceSelect } from '@/console/features/rbac/components/resource-selectors/console-resource-select';
import { consoleRbacPermissionChecks } from '@/console/features/rbac/config/console-rbac-permission-checks';
import { ResourceTypeEnum } from '@/shared/components/ui/resource-icon/resource-icon.types';
import { usePermissions } from '@/shared/features/ability/hooks/use-permissions/use-permissions';
import { POLICIES } from '@/shared/features/rbac/components/policies/policies.constants';
import {
  TRbacConfig,
  TRbacResourceSelectors,
  TRbacTenantSettings,
} from '@/shared/features/rbac/context/rbac-config-context.types';
import {
  useCreateUserMutation,
  useCreateUserRoleMutation,
  useDeleteRoleMutation,
  useDeleteUserMutation,
  useLinkRoleToUserMutation,
  useReadAllRolesQuery,
  useReadAllUsersQuery,
  useReadPoliciesForOwnUserQuery,
  useReadPoliciesForUserQuery,
  useReadRoleQuery,
  useReadTenantQuery,
  useReadUserQuery,
  useReadUserRoleLinksForRoleQuery,
  useReadUserRoleLinksForUserQuery,
  useReadUsersForRoleQuery,
  useReadUserUiPreferencesQuery,
  useRemoveRoleFromUserMutation,
  useUpdateUserMutation,
  useUpdateUserRoleLinkMutation,
  useUpdateUserRoleMutation,
  useUpdateUserUiPreferencesMutation,
} from '@/shared/redux/api/ident/enhanced-ident-api';

const useConsoleTenantSettings = (): TRbacTenantSettings => {
  const { data, isLoading } = useReadTenantQuery();

  return {
    ssoEnabled: data?.readTenant?.ssoEnabled ?? false,
    manageSsoUsersInSda: data?.readTenant?.manageSsoUsersInSda ?? false,
    maxUserRoleExpirationDays: data?.readTenant?.maxUserRoleExpirationDays,
    companyName: data?.readTenant?.companyName,
    ownerId: data?.readTenant?.ownerId,
    isLoadingTenant: isLoading,
  };
};

const useConsoleCanReadRole = (): boolean =>
  usePermissions({ action: 'readUserRole', entity: 'Ident' });

const CONSOLE_RESOURCES = [
  ResourceTypeEnum.Device,
  ResourceTypeEnum.Gateway,
  ResourceTypeEnum.Document,
  ResourceTypeEnum.Integration,
  ResourceTypeEnum.License,
  ResourceTypeEnum.Vault,
  ResourceTypeEnum.Project,
  ResourceTypeEnum.Pipeline,
  ResourceTypeEnum.Webhook,
];

const resourceSelectors = Object.fromEntries(
  CONSOLE_RESOURCES.map((resourceType) => [
    resourceType,
    ConsoleResourceSelect,
  ]),
) as TRbacResourceSelectors;

export const consoleRbacConfig: TRbacConfig = {
  api: {
    useReadAllRolesQuery,
    useReadRoleQuery,
    useReadAllUsersQuery,
    useReadUserQuery,
    useReadUsersForRoleQuery,
    useReadUserRoleLinksForUserQuery,
    useReadUserRoleLinksForRoleQuery,
    useReadPoliciesForUserQuery,
    useReadOwnUserRolesQuery: useReadPoliciesForOwnUserQuery,
    useCreateUserMutation,
    useUpdateUserMutation,
    useDeleteUserMutation,
    useCreateUserRoleMutation,
    useUpdateUserRoleMutation,
    useDeleteRoleMutation,
    useLinkRoleToUserMutation,
    useRemoveRoleFromUserMutation,
    useUpdateUserRoleLinkMutation,
    useReadUserUiPreferencesQuery,
    useUpdateUserUiPreferencesMutation,
  },
  policies: POLICIES,
  resourceSelectors,
  resourceAggregations: { group: true, tags: true },
  useTenantSettings: useConsoleTenantSettings,
  useCanReadRole: useConsoleCanReadRole,
  rbacPermissionChecks: consoleRbacPermissionChecks,
};
